/**
 * What a view that reads on sight should mark read — a calculation.
 *
 * A view reads exactly what it shows. A comment's replies live in their own thread, so:
 *
 * - **The full thread** reads up to its newest loaded message, and is behind while that
 *   message is newer than the read cursor (ONE id across the whole channel).
 * - **The comment list** (roots only) reads the top-level messages up to its newest one —
 *   not their replies.
 * - **One reply thread** reads its root and its replies up to its newest reply — not the
 *   other threads.
 *
 * The two partial views send a `scope`, which records receipts and moves no cursor, so the
 * cursor cannot tell them they are behind: they read once per newest message on screen,
 * which the server makes idempotent. Reading the whole channel from either — what they once
 * did — marked every other thread's replies read the moment one thread was opened.
 */

import type { MarkChannelReadInput } from "../_api/channel-endpoints";
import type { ReadCursorId } from "./unread-boundary";

/** The read a view sends once it has been looked at; `null` reads nothing. */
export type ReadOnViewTarget = MarkChannelReadInput | null;

export type ReadOnViewInput =
  | {
      view: "channel";
      /** Newest persisted loaded message, or `null` when nothing is loaded. */
      newestMessageId: string | null;
      /** `undefined` while the cursor is still loading. */
      lastReadMessageId: ReadCursorId;
    }
  | { view: "roots"; newestMessageId: string | null }
  | {
      view: "thread";
      /** The thread's root. */
      parentId: string;
      newestMessageId: string | null;
    };

export function computeReadOnViewTarget(
  input: ReadOnViewInput,
): ReadOnViewTarget {
  const { newestMessageId } = input;
  if (newestMessageId == null) return null;

  switch (input.view) {
    case "channel":
      // Time-sortable nanoids of equal length: string order is creation order.
      return input.lastReadMessageId == null ||
        newestMessageId > input.lastReadMessageId
        ? { messageId: newestMessageId }
        : null;
    case "roots":
      return { messageId: newestMessageId, scope: { kind: "roots" } };
    case "thread":
      return {
        messageId: newestMessageId,
        scope: { kind: "thread", parentId: input.parentId },
      };
  }
}
